import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Form } from 'antd';
import { LocalizationContext } from '../../../hoc/Localization';
import { SettingsContext } from '../../../hoc/Settings';
import { UserContext } from '../../../hoc/User';
import api from '../../../utils/api';
import Button from '../../Button';
import Input from '../../Input';
import TextArea from '../../Input/TextArea';
import Login from '../../Auth/Login';
import styles from './styles.less';
import Router from '../../../routes';
import usePriceFormat from '../../../utils/priceFormat';
import PaymentProcess from '../../PaymentProcess';
import PaymentAmount from '../PaymentAmout';

const Register = ({
  orderCourse,
  orderLoading,
  course,
  selectedSchedule,
  justRequested,
  setJustRequested,
  isRegistered,
  useSchoolPayment,
}) => {
  const { t, locale } = React.useContext(LocalizationContext);
  const { user, refreshUser } = React.useContext(UserContext);
  const {
    domainSettings: { domain, schoolId },
  } = React.useContext(SettingsContext);

  const [status, setStatus] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [coursePath, setCoursePath] = React.useState('');
  const [registerStep, setRegisterStep] = React.useState(1);

  const [form] = Form.useForm();
  const {
    school: { isOnlinePaymentEnable },
    price,
    category,
    shortId,
    currency,
  } = course;

  React.useEffect(() => {
    setCoursePath(`${domain}${window.location.pathname}?scroll=true`);
  }, []);

  React.useEffect(() => {
    if (user && user.requestEmail) {
      form.setFieldsValue({
        phone: user.requestPhone,
        email: user.requestEmail,
      });
    }
  }, [user]);

  React.useEffect(() => {
    const isJustRequested = justRequested.indexOf(selectedSchedule.id) !== -1;
    if (((isRegistered && registerStep === 1) || isJustRequested) && price) {
      setRegisterStep(2);
    }
    if ((!isRegistered && !isJustRequested) || !price) {
      setRegisterStep(1);
    }
  }, [isRegistered, justRequested]);

  function buttonText() {
    if (isOnlinePaymentEnable && price) {
      return t('course.registration.checkout');
    }
    return t('course.registration.register');
  }

  const handleSubmit = async data => {
    setLoading(true);
    if (!user && !data) {
      setLoading(false);
      return;
    }
    const { school } = course;
    const courseToSave = { ...course };
    const propsToKeep = [
      'title',
      'name',
      'price',
      'id',
      'instructions',
      'trainingType',
      'location',
      'redirect_uri_book',
      'selfLearning',
      'shortId',
    ];
    Object.keys(courseToSave).forEach(key => {
      if (!propsToKeep.includes(key)) {
        delete courseToSave[key];
      }
    });

    courseToSave.selectedSchedule = selectedSchedule;
    const query = {
      school_id: school.external_id,
      school_name: school.title,
      school_logo: school.logo,
      school_email: school.email,
      school_shortId: school.shortId,
      course_id: course.id,
      course_name: course.title,
      course_shortId: course.shortId,
      course_external_id: course.external_id,
      course: courseToSave,
      phone: data.phone,
      email: data.email,
      startDate: (selectedSchedule && selectedSchedule.start_date) || '',
      endDate: (selectedSchedule && selectedSchedule.end_date) || '',
      notes: data.note,
      requestedByUser: true,
      category: category.title,
      self_learning: course.self_learning,
      courseUrl: coursePath,
      schoolId,
    };
    (async () => {
      if (registerStep === 1) {
        const res = await api.post('/users/course-request', query);
        setStatus(res.status);
        setLoading(false);
        refreshUser();

        const {
          location: { country },
        } = course;
        api
          .post('user-subscriptions', {
            email: user.email,
            categories: [category.name] || [],
          })
          .catch(() => null);
        const testExpression = '#test';
        if (
          !(course.title || '').includes(testExpression) &&
          !(data.note || '').includes(testExpression)
        ) {
          window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
              purchase: {
                actionField: {
                  id: `${course.id}-${new Date().getTime()}`, // (request id)                        // Transaction ID. Required for purchases and refunds.
                  // 'affiliation': 'Online Store', // 'Request|Purchase'
                  revenue: Math.round(course.price / (currency === 'AMD' ? 480 : 1)), // course price                    // Total transaction value (incl. tax and shipping)
                  tax: '0', // 0
                  shipping: '0', // 0
                  // 'coupon': 'SUMMER_SALE' // keep empty
                },
                products: [
                  {
                    // es mi hata linelu mer depqum                            // List of productFieldObjects.
                    name: course.title, // course name     // Name or ID is required.
                    id: course.id, // course external id
                    price: Math.round(course.price / (currency === 'AMD' ? 480 : 1)), // price
                    brand: school.title, // school name
                    category:
                      (course.language && course.language.Name) ||
                      (course.coursetypes && course.coursetypes.title) ||
                      '', // course language
                    // 'variant': 'Gray', // empty
                    quantity: 1, // 1
                    country,
                    // 'coupon': ''                            // Optional fields may be omitted or set to empty string.
                  },
                ],
              },
            },
          });
        }
        if (res.status === 'success') {
          if (course.redirect_uri_book) {
            window.location.replace(course.redirect_uri_book);
          } else {
            let externalId = '';
            if (res.data.self_learning) {
              externalId = res.data.external_id;
            }
            if (!isOnlinePaymentEnable || !price) {
              Router.pushRoute('success', { id: shortId, locale, externalId });
            } else {
              setJustRequested([...justRequested, selectedSchedule.id]);
            }
          }
        }
      }
    })();
  };
  const symbolCurrency = {
    AMD: '֏',
    USD: '$',
    EUR: '€',
    RUB: '₽',
  };
  const { priceFormat } = usePriceFormat();
  return (
    <div id="registerAndPayment" className={styles.container}>
      <div className={styles.registreTitle}>
        {registerStep === 2 ? (
          <div className={styles.secondStepTitle}>
            <h2 className={styles.firstTitle}>{t('course.payment.confirmation.text')}</h2>
            <PaymentAmount course={course} />
          </div>
        ) : (
            <div className={styles.firstStepTitle}>
              <h2 className={styles.firstTitle}>{t('frontpage.step.title.4')}</h2>
              <h3 className={styles.secondTitle}>{t('course.registration.user.info')}</h3>
            </div>
          )}
      </div>
      {registerStep === 2 ? (
        <div className={styles.secondStep}>
          {!!isOnlinePaymentEnable && !!price && (
            <>
              <div className={`${styles.col} ${styles.pay}`}>
                <div className={styles.paymantProcessText}>
                  {t('course.register.chose.your.payment.option')}
                </div>
                <PaymentProcess
                  orderCourse={orderCourse}
                  orderLoading={orderLoading}
                  course={course}
                  useSchoolPayment={useSchoolPayment}
                />
              </div>
            </>
          )}
        </div>
      ) : (
          <div className={styles.firstStep}>
            <Form
              onFinish={handleSubmit}
              form={form}
              name="basic"
              className={styles.registerCourseForm}
            >
              <Form.Item
                name="phone"
                rules={[
                  {
                    required: true,
                    pattern: /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/,
                    message: (
                      <div className={styles.warning}>
                        <img src="/static/images/course/ios-warning.svg" alt="warning! " />
                        <span>{t('form.validation.invalidPhone')}</span>
                      </div>
                    ),
                  },
                ]}
              >
                <Input
                  name="register_course_phone"
                  version="long"
                  size="large"
                  placeholder={t('wizard.phone.number')}
                />
              </Form.Item>
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: (
                      <div className={styles.warning}>
                        <img src="/static/images/course/ios-warning.svg" alt="warning! " />
                        <span>{t('form.validation.invalidEmail')}</span>
                      </div>
                    ),
                  },
                ]}
              >
                <Input
                  name="register_course_email"
                  version="long"
                  size="large"
                  placeholder={t('register.course.email')}
                />
              </Form.Item>
              {status === 'success' && !isOnlinePaymentEnable ? (
                <Alert message={t('addschool.request.success')} type="success" />
              ) : null}
              {status !== 'success' && status !== '' ? (
                <Alert message={t('addschool.request.error')} type="error" />
              ) : null}
              <Form.Item name="note">
                <TextArea name="register_course_notes" placeholder={t('form.notes')} />
              </Form.Item>
              <div className={styles.buttons}>
                <Form.Item shouldUpdate>
                  {/* eslint-disable-next-line */}
                  {() =>
                    !user ? (
                      <Login onLogin={handleSubmit}>
                        <Button
                          type="secondary"
                          htmlType="submit"
                          disabled={
                            !form.isFieldTouched('phone') ||
                            !form.isFieldTouched('email') ||
                            form.getFieldsError().filter(({ errors }) => errors.length).length
                          }
                          data_gh="register_course"
                        >
                          {buttonText()}
                        </Button>
                      </Login>
                    ) : (
                        <Button
                          type="primary"
                          htmlType="submit"
                          disabled={
                            !form.getFieldValue('phone') ||
                            !form.getFieldValue('email') ||
                            form.getFieldsError().filter(({ errors }) => errors.length).length
                          }
                          loading={loading}
                          data_gh="register_course"
                        >
                          {buttonText()}
                        </Button>
                      )}
                </Form.Item>
              </div>
            </Form>
          </div>
        )}
    </div>
  );
};

Register.propTypes = {
  orderCourse: PropTypes.func.isRequired,
  selectedSchedule: PropTypes.object,
  orderLoading: PropTypes.bool,
  isRegistered: PropTypes.bool,
  course: PropTypes.object.isRequired,
  justRequested: PropTypes.array,
  setJustRequested: PropTypes.func,
  useSchoolPayment: PropTypes.bool,
};

Register.defaultProps = {
  orderLoading: false,
  selectedSchedule: null,
  isRegistered: false,
  justRequested: [],
  setJustRequested: () => { },
  useSchoolPayment: false,
};

export default Register;
